// my-tickers/client/src/hooks/useCategoriesTree.ts
import { useMemo } from 'react'
import type { Category, QuoteSnapshot, Ticker } from '../types.ts'

export const ROOT_CATEGORY_ID = 0
export const ROOT_CATEGORY_NAME = 'ROOT'

export type TickerWithValue = Ticker & {
  value: number | null
}

export type CategoryTreeNode = Category & {
  isRoot: boolean
  children: CategoryTreeNode[]
  totalValue: number | null
  hasNullQuotes: boolean
}

export type CategoriesTreeResult = {
  tree: CategoryTreeNode[]
  tickersByCategory: Map<number, TickerWithValue[]>
}

// Deriva o valor atual do ticker usando quantidade e cotação, quando disponíveis.
export function calculateTickerValue(ticker: Ticker, quote?: QuoteSnapshot): number | null {
  const qtd = Number(ticker.qtd)
  const price = quote?.regularMarketPrice

  if (!Number.isFinite(qtd) || price === null || price === undefined || Number.isNaN(price)) {
    return null
  }

  return qtd * price
}

// Monta a hierarquia pai/filho de categorias a partir da lista plana.
function buildCategoryTree(categories: Category[]): CategoryTreeNode[] {
  const byId = new Map<number, CategoryTreeNode>()

  for (const category of categories) {
    byId.set(category.id, {
      ...category,
      isRoot: false,
      children: [],
      totalValue: null,
      hasNullQuotes: false,
    })
  }

  const roots: CategoryTreeNode[] = []

  for (const node of byId.values()) {
    if (node.parent_id === null) {
      roots.push(node)
      continue
    }

    const parent = byId.get(node.parent_id)
    if (!parent) {
      roots.push(node)
      continue
    }

    parent.children.push(node)
  }

  const sortNodes = (nodes: CategoryTreeNode[]) => {
    nodes.sort((a, b) => a.name.localeCompare(b.name))
    for (const item of nodes) {
      sortNodes(item.children)
    }
  }

  sortNodes(roots)

  return roots
}

// Função pura que consolida o modelo derivado consumido pelas visualizações.
export function buildCategoriesTree({
  categories,
  tickers,
  quotesBySymbol,
}: {
  categories: Category[]
  tickers: Ticker[]
  quotesBySymbol: Record<string, QuoteSnapshot>
}): CategoriesTreeResult {
  // Enriquece tickers com o valor calculado a partir das cotações atuais.
  const tickersWithValue = tickers.map((ticker) => ({
    ...ticker,
    value: calculateTickerValue(ticker, quotesBySymbol[ticker.ticker]),
  }))

  // Agrupa tickers pelas categorias para uso compartilhado entre tabela e charts.
  const tickersByCategory = new Map<number, TickerWithValue[]>()

  for (const ticker of tickersWithValue) {
    const categoryId = ticker.category_id ?? ROOT_CATEGORY_ID
    const current = tickersByCategory.get(categoryId) ?? []
    current.push(ticker)
    tickersByCategory.set(categoryId, current)
  }

  for (const tickerList of tickersByCategory.values()) {
    tickerList.sort((a, b) => a.ticker.localeCompare(b.ticker))
  }

  const tree = buildCategoryTree(categories)
  const syntheticRoot: CategoryTreeNode = {
    id: ROOT_CATEGORY_ID,
    user_id: categories[0]?.user_id ?? tickers[0]?.user_id ?? 0,
    parent_id: null,
    name: ROOT_CATEGORY_NAME,
    slug: 'root',
    created_at: '',
    updated_at: '',
    isRoot: true,
    children: tree,
    totalValue: null,
    hasNullQuotes: false,
  }

  // Propaga os totais da folha para a raiz, somando tickers próprios e subcategorias.
  const assignTotals = (nodes: CategoryTreeNode[]) => {
    for (const node of nodes) {
      assignTotals(node.children)

      const ownTickers = tickersByCategory.get(node.id) ?? []
      const ownTotal = ownTickers.reduce((sum, ticker) => {
        return sum + (ticker.value ?? 0)
      }, 0)
      const ownHasNullQuotes = ownTickers.some((ticker) => ticker.value === null)

      const childrenTotal = node.children.reduce((sum, child) => {
        return sum + (child.totalValue ?? 0)
      }, 0)
      const childrenHaveNullQuotes = node.children.some((child) => child.hasNullQuotes)

      node.totalValue = ownTotal + childrenTotal
      node.hasNullQuotes = ownHasNullQuotes || childrenHaveNullQuotes
    }
  }

  assignTotals([syntheticRoot])

  return {
    tree: [syntheticRoot],
    tickersByCategory,
  }
}

// Hook de domínio: memoiza o modelo derivado sem fazer fetch nem gerenciar estado visual.
export function useCategoriesTree({
  categories,
  tickers,
  quotesBySymbol,
}: {
  categories: Category[]
  tickers: Ticker[]
  quotesBySymbol: Record<string, QuoteSnapshot>
}) {
  return useMemo(
    () =>
      buildCategoriesTree({
        categories,
        tickers,
        quotesBySymbol,
      }),
    [categories, tickers, quotesBySymbol],
  )
}
